// Quiz promotions (backend promo_quiz.py, migration 109): "Here's 50% off if you answer this question".
// QuizOfferCard sits on the locked-page card (SubscriptionGuard) and QuizDialog also opens from the site banner
// (DiscountBanner) when the bannered discount is a quiz. A right answer gives the person their own single-use code,
// which /app/billing?promo=CODE applies at checkout.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Dialog, DialogContent, Typography } from '@mui/material';

const API = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

function call(path, method) {
  const token = localStorage.getItem('token');
  return fetch(API + '/api/billing' + path, {
    method: method || 'GET',
    headers: Object.assign({ 'Content-Type': 'application/json' }, token ? { Authorization: 'Bearer ' + token } : {}),
  }).then(function(r) { return r.json().then(function(d) { return { ok: r.ok, status: r.status, data: d }; }); });
}

function answer(attemptId, choice) {
  const token = localStorage.getItem('token');
  return fetch(API + '/api/billing/quiz/attempt/' + attemptId, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    body: JSON.stringify({ choice: choice }),
  }).then(function(r) { return r.json(); });
}

/** The quiz itself: one question at a time, 3 tries a day, the code on a right answer. */
export function QuizDialog({ offer, open, onClose }) {
  const navigate = useNavigate();
  const [q, setQ] = useState(null);           // {attempt_id, question, choices}
  const [result, setResult] = useState(null); // answer response
  const [picked, setPicked] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  function next() {
    setQ(null); setResult(null); setPicked(null); setMsg(''); setBusy(true);
    call('/quiz/' + offer.promo_id + '/question', 'POST').then(function(r) {
      if (r.ok) setQ(r.data); else setMsg(r.data.message || 'No question right now.');
    }).catch(function() { setMsg('Couldn’t load a question. Try again.'); }).finally(function() { setBusy(false); });
  }

  useEffect(function() { if (open && offer) next(); }, [open, offer && offer.promo_id]); // eslint-disable-line react-hooks/exhaustive-deps

  function pick(i) {
    if (result || busy) return;
    setPicked(i); setBusy(true);
    answer(q.attempt_id, i).then(setResult).catch(function() { setMsg('Couldn’t send your answer. Try again.'); })
      .finally(function() { setBusy(false); });
  }

  if (!offer) return null;
  const won = result && result.won;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '18px' } }}>
      <DialogContent sx={{ p: 3 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#0e9f6e', mb: 1 }}>
          🎯 Quick question · {offer.discount}
        </Typography>
        {msg && <Typography sx={{ fontSize: 15, my: 2 }}>{msg}</Typography>}
        {!q && !msg && <Typography sx={{ fontSize: 14, color: 'text.secondary', my: 2 }}>Loading a question…</Typography>}
        {q && (
          <>
            <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.35, mb: 2 }}>{q.question}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {q.choices.map(function(c, i) {
                const right = result && result.right_choice === i;
                const wrongPick = result && picked === i && !result.correct;
                return (
                  <Button key={i} onClick={function() { pick(i); }} disabled={!!result && !right && !wrongPick}
                    variant="outlined" sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', fontSize: 15, py: 1.2, borderRadius: '12px',
                      borderColor: right ? '#0e9f6e' : wrongPick ? '#d93025' : 'divider', color: right ? '#0e9f6e' : wrongPick ? '#d93025' : 'text.primary',
                      bgcolor: right ? 'rgba(14,159,110,0.08)' : wrongPick ? 'rgba(217,48,37,0.06)' : 'transparent' }}>
                    {c}
                  </Button>
                );
              })}
            </Box>
          </>
        )}
        {result && (
          <Box sx={{ mt: 2 }}>
            {result.explanation && <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 1.5 }}>{result.explanation}</Typography>}
            {won ? (
              <>
                <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 0.5 }}>Nice one! 🎉 Here’s your {won.discount}:</Typography>
                <Box sx={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 20, fontWeight: 700, letterSpacing: '0.06em', p: 1.5, my: 1,
                  textAlign: 'center', borderRadius: '12px', border: '1px dashed #0e9f6e', color: '#0e9f6e' }}>{won.code}</Box>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>
                  Yours only, one use, valid until {new Date(won.valid_until).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}.
                </Typography>
                <Button fullWidth variant="contained" sx={{ borderRadius: '12px', bgcolor: '#0e9f6e', '&:hover': { bgcolor: '#0b8a5f' } }}
                  onClick={function() { onClose(); navigate('/app/billing?promo=' + encodeURIComponent(won.code)); }}>
                  Use it now
                </Button>
              </>
            ) : result.correct ? (
              <Typography sx={{ fontSize: 15 }}>Right! {result.ended ? 'This offer just ended, sorry.' : ''}</Typography>
            ) : (
              <>
                <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 1.5 }}>Not quite.</Typography>
                {result.tries_left > 0
                  ? <Button fullWidth variant="outlined" sx={{ borderRadius: '12px' }} onClick={next}>Try another question ({result.tries_left} left today)</Button>
                  : <Typography sx={{ fontSize: 13.5, color: 'inherit', opacity: 0.75 }}>That’s it for today. Come back tomorrow for new questions.</Typography>}
              </>
            )}
          </Box>
        )}
        <Button fullWidth size="small" color="inherit" sx={{ mt: 1.5 }} onClick={onClose}>Close</Button>
      </DialogContent>
    </Dialog>
  );
}

/** The offer on a locked page: "Here's 50% off if you answer this question", or my code if I already won. */
export function QuizOfferCard({ placement = 'locked', intro = null }) {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  function load() { call('/quiz/active?placement=' + placement).then(function(r) { if (r.ok) setState(r.data); }).catch(function() {}); }
  useEffect(load, [placement]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!state || !state.offer) return null;
  const won = state.won && !state.won.used ? state.won : null;
  return (
    <>
    {intro}
    <Box sx={{ my: 2, p: 2, borderRadius: '14px', textAlign: 'left', border: '1px solid rgba(14,159,110,0.35)', bgcolor: 'rgba(14,159,110,0.07)' }}>
      <Typography sx={{ fontWeight: 700, fontSize: 15.5, mb: 0.5 }}>🎯 {state.offer.headline}</Typography>
      {won ? (
        <>
          <Typography sx={{ fontSize: 13.5, color: 'inherit', opacity: 0.75, mb: 1.2 }}>
            You won <strong>{won.code}</strong> ({state.offer.discount}). Use it before {new Date(won.valid_until).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}.
          </Typography>
          <Button size="small" variant="contained" sx={{ borderRadius: '10px', bgcolor: '#0e9f6e', '&:hover': { bgcolor: '#0b8a5f' } }}
            onClick={function() { navigate('/app/billing?promo=' + encodeURIComponent(won.code)); }}>Use my code</Button>
        </>
      ) : state.tries_left === 0 ? (
        <Typography sx={{ fontSize: 13.5, color: 'inherit', opacity: 0.75 }}>You’ve had today’s 3 tries. New questions tomorrow!</Typography>
      ) : (
        <>
          <Typography sx={{ fontSize: 13.5, color: 'inherit', opacity: 0.75, mb: 1.2 }}>One simple trading question. Get it right and it’s yours.</Typography>
          <Button size="small" variant="contained" sx={{ borderRadius: '10px', bgcolor: '#0e9f6e', '&:hover': { bgcolor: '#0b8a5f' } }}
            onClick={function() { setOpen(true); }}>Take the question</Button>
        </>
      )}
      <QuizDialog offer={state.offer} open={open} onClose={function() { setOpen(false); load(); }} />
    </Box>
    </>
  );
}
